import React from "react";

import cx from "classnames";

import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";

import styles from "./ToggleButton.scss";

export interface ToggleButtonProps {
  /**
   * Whether the button is active (toggled) or not.
   */
  active?: boolean;

  /**
   * Whether the button should be disabled and not clickable.
   */
  disabled?: boolean;

  /**
   * Key of the icon to be displayed (see Icon Library).
   */
  icon: IconDictionary;

  /**
   * Button description used for tooltips and ARIA labeling.
   */
  label: string;

  /**
   * Function to toggle state, invoked when button is clicked.
   */
  onToggle: (newActive: boolean) => void;

  /**
   * Optional test ID for testing purposes.
   */
  "data-testid"?: string;

  /**
   * Additional class names for the button.
   */
  className?: string;

  /**
   * The type of the button.
   */
  type?: "button" | "submit" | "reset" | undefined;
  /**
   * Reference to the button element.
   */
  innerRef?: React.Ref<HTMLButtonElement>;
}

/**
 * A controlled button component with a persistent active/inactive state.
 */
export function ToggleButton({
  active = false,
  disabled = false,
  icon,
  label,
  onToggle,
  className,
  innerRef,
  ...buttonProps
}: ToggleButtonProps & React.HTMLProps<HTMLButtonElement>) {
  const buttonClassName = cx(
    styles.togglebutton,
    { [styles["selected"]]: active },
    className
  );

  return (
    <button
      ref={innerRef}
      aria-pressed={active}
      title={label}
      aria-label={label}
      className={buttonClassName}
      onClick={() => onToggle(!active)}
      disabled={disabled}
      {...buttonProps}
    >
      <Icon icon={icon} size={16} color="currentColor" />
    </button>
  );
}
